import {expandRecurring} from './recurrence';

// Calendar bookings affect scheduling suggestions, never saved availability.
export function bookingParticipantUids(event, members) {
  if(event.cancelled||event.canceled||['cancelled','canceled'].includes(String(event.status).toLowerCase()))return [];
  const included=uid=>String(event.participantStates?.[uid]?.status).toLowerCase()!=='declined';
  if (Array.isArray(event.participantsUid) && event.participantsUid.length) return [...new Set(event.participantsUid)].filter(included);
  const names = new Set((event.participants || []).map(name => String(name).toLowerCase()));
  return members.filter(member => names.has(String(member.name).toLowerCase())&&included(member.uid)).map(member => member.uid);
}

export function findBookingConflicts(events, draft, members) {
  const start = Date.parse(draft.startUTC), end = Date.parse(draft.endUTC);
  const selected = new Set(draft.participantsUid || []);
  return expandRecurring(events.filter(event => event.calendar === 'team'), new Date(start - 86400000), new Date(end))
    .filter(event => event.eventId !== draft.eventId && Date.parse(event.startUTC) < end && Date.parse(event.endUTC) > start &&
      bookingParticipantUids(event, members).some(uid => selected.has(uid)));
}

export function subtractBookings(availability, bookings) {
  return Object.fromEntries(Object.entries(availability).map(([uid, segments]) => [uid,
    segments.flatMap(segment => {
      let parts = [segment];
      for (const booking of bookings) {
        if (booking.day !== segment.day || !booking.uids.includes(uid)) continue;
        parts = parts.flatMap(part => {
          if (booking.endMin <= part.startMin || booking.startMin >= part.endMin) return [part];
          const result = [];
          if (booking.startMin > part.startMin) result.push({...part, endMin: booking.startMin});
          if (booking.endMin < part.endMin) result.push({...part, startMin: booking.endMin});
          return result;
        });
      }
      return parts;
    }),
  ]));
}

// Partition an overlap at booking boundaries without treating a partial roster
// booking as if every member were occupied. Intervals are half-open.
export function overlapBookingParts(window,bookings=[]) {
  const relevant=bookings.filter(row=>row.startMin<window.toMin&&row.endMin>window.fromMin&&row.uids.some(uid=>window.uids.includes(uid)));
  if(!relevant.length)return [];
  const edges=[...new Set([window.fromMin,window.toMin,...relevant.flatMap(row=>[Math.max(window.fromMin,row.startMin),Math.min(window.toMin,row.endMin)])])].sort((a,b)=>a-b);
  return edges.slice(0,-1).map((fromMin,index)=>{
    const toMin=edges[index+1],active=relevant.filter(row=>row.startMin<toMin&&row.endMin>fromMin);
    const bookedUids=window.uids.filter(uid=>active.some(row=>row.uids.includes(uid)));
    return {fromMin,toMin,bookedUids,freeUids:window.uids.filter(uid=>!bookedUids.includes(uid)),bookings:active};
  });
}

export function availabilityDayLanes(byUid,members) {
  return Array.from({length:7},(_,day)=>members.filter(member=>(byUid[member.uid]||[]).some(row=>row.day===day)).map(member=>member.uid));
}

// Pack simultaneous events into separate lanes, keeping the date/time geometry.
export function bookingLanes(segments) {
  const result=[];
  for(let day=0;day<7;day++){
    let group=[],groupEnd=-1,ends=[];
    const flush=()=>{group.forEach(row=>result.push({...row,laneCount:ends.length}));group=[];ends=[];};
    for(const segment of segments.filter(row=>row.day===day).sort((a,b)=>a.startMin-b.startMin||b.endMin-a.endMin)){
      if(segment.startMin>=groupEnd){flush();groupEnd=-1;}
      let lane=ends.findIndex(end=>end<=segment.startMin);if(lane<0)lane=ends.length;
      ends[lane]=segment.endMin;groupEnd=Math.max(groupEnd,segment.endMin);group.push({...segment,lane});
    }
    flush();
  }
  return result;
}
